import React from 'react';
import { Box, Paper, ButtonBase, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { PASSPORT_NAV_ITEMS } from '../../data/passportNavigation';
import { passportColors, passportLayout } from '../../design/passportTokens';
import { useLanguage } from '../../context/LanguageContext';

export const PassportBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm')); // Only show on phone < 600px
  const { t } = useLanguage();

  if (!isPhone) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 1150,
        display: 'flex',
        justifyContent: 'center',
        bgcolor: 'rgba(12, 10, 8, 0.95)',
        backdropFilter: 'blur(16px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        pb: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          maxWidth: '600px',
          bgcolor: 'transparent',
          borderRadius: 0,
          px: 1,
          height: `${passportLayout.mobileNavHeight}px`,
        }}
      >
        {PASSPORT_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const activePath = item.activePath || item.path;
          const isActive = item.exact
            ? location.pathname === activePath
            : location.pathname === activePath || location.pathname.startsWith(`${activePath}/`);
          // The visible caption used the hardcoded English shortLabel, so the
          // bar stayed in English while the rest of the UI switched to French.
          const label = t(item.key || `gigPassport.nav.${item.id}`);
          const shortLabel = item.shortKey ? t(item.shortKey) : (item.shortLabel || label);
          return (
            <ButtonBase
              key={item.id}
              onClick={() => navigate(`${item.path}${location.search}`)}
              aria-label={label} // Full label for screen readers
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                minWidth: 0,
                height: '100%',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: isActive 
                  ? passportColors.cyan 
                  : 'rgba(255, 255, 255, 0.45)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon sx={{ fontSize: '1.25rem' }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  width: '100%',
                  mt: 0.5,
                  px: 0.25,
                  fontSize: '0.65rem',
                  lineHeight: 1,
                  fontWeight: isActive ? 700 : 500,
                  // Sentence case and no extra tracking. Uppercase plus
                  // letter-spacing is what pushed "Passport" past the width of
                  // a fifth of a 320px screen and truncated it to "PASSPO…".
                  fontFamily: '"Space Grotesk", sans-serif',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                }}
              >
                {shortLabel}
              </Typography>
            </ButtonBase>
          );
        })}
      </Paper>
    </Box>
  );
};

export default PassportBottomNav;
